import {useCodeUtilisateurEffectif} from '@/hooks/auth/usePermissionsCommerciales';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {InfoIcon} from '@chakra-ui/icons';
import {Box, Button, HStack, Text} from '@chakra-ui/react';
import {HaliRenderMany, HaliRenderOne, useCommand} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {ConversationDesc} from 'api-types/ffconnect-hal';
import {useEnvoyerMessageAvecPiecesJointes} from 'ff-common';
import {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {ConversationMessageInputArea} from '@/backoffice/domains/CanalCollaboratif/ConversationMessageInputArea';
import {ConversationMessages} from '@/backoffice/domains/CanalCollaboratif/ConversationMessages';
import {MessageListSkeleton} from '@/backoffice/domains/CanalCollaboratif/ConversationsSkeletons';
import {ConversationClientHeader} from './ConversationClientHeader';

interface ConversationClientDetailConversationViewProps {
  conversationNav: Navigable<ConversationDesc>;
  onRetour: () => void;
  onFermer: () => void;
  // Après "Rejoindre", on revient à la liste pour que la conversation réapparaisse dans "Mes conversations".
  onConversationRejointe?: () => void;
}

/**
 * Vue de détail d'une conversation "périmètre" dans la popin (onglet "Toutes les conversations").
 * Utilisée par le commercial pour une conversation de son périmètre dont il n'est pas forcément
 * participant : lecture seule + bouton "Rejoindre" si non participant, saisie normale sinon.
 */
export const ConversationClientDetailConversationView: React.FC<
  ConversationClientDetailConversationViewProps
> = ({conversationNav, onRetour, onFermer, onConversationRejointe}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Code utilisateur effectif (pour aligner les bulles : perspective du commercial, pas du client)
  const codeUtilisateur = useCodeUtilisateurEffectif();

  // Hook d'envoi de message (utilisé uniquement si l'utilisateur est participant)
  const {
    messageContent,
    setMessageContent,
    selectedFiles,
    isSubmitting,
    peutEnvoyer,
    handleFileSelect,
    handleRemoveFile,
    envoyerMessage,
    documentInputRef,
    imageInputRef,
    textareaRef,
  } = useEnvoyerMessageAvecPiecesJointes(conversationNav, {
    onError,
    onFichierTropVolumineux: (nom) => {
      textToast(t('global.fichier_trop_volumineux', {nom}));
    },
  });

  // Commande pour rejoindre la conversation (s'auto-ajouter comme participant)
  const {submit: rejoindreConversation, isSubmitting: isRejoindreSubmitting} =
    useCommand(conversationNav, 'c:ajouter-participant', {
      onSuccess: () => {
        textToast(t('back_office.conversations.vous_avez_rejoint'));
        onConversationRejointe?.();
      },
      onError,
    });

  const skeleton = (
    <Box
      display='flex'
      flexDirection='column'
      h='100%'
    >
      <ConversationClientHeader
        titre='...'
        onRetour={onRetour}
        onFermer={onFermer}
      />
      <Box
        flex={1}
        p={4}
      >
        <MessageListSkeleton />
      </Box>
    </Box>
  );

  return (
    <HaliRenderOne
      nav={conversationNav}
      skeleton={() => skeleton}
      render={({state: conversationState}) => {
        const conversation = conversationState.data;
        // Participant si la commande d'envoi de message est présente.
        const isParticipant = conversationState.hasCommand('c:envoyer-message');

        return (
          <Box
            display='flex'
            flexDirection='column'
            h='100%'
          >
            {/* Header */}
            <ConversationClientHeader
              titre={
                conversation.titre ?? t('client.conversations.titre_defaut')
              }
              onRetour={onRetour}
              onFermer={onFermer}
            />

            {/* Bandeau lecture seule (non participant) */}
            {!isParticipant && (
              <Box
                bg='blue.bg_light'
                px={4}
                py={3}
                borderBottom='1px solid'
                borderColor='gray.light'
              >
                <HStack
                  spacing={2}
                  align='center'
                >
                  <InfoIcon
                    color='blue.main'
                    boxSize={4}
                  />
                  <Text
                    fontSize='sm'
                    color='blue.main'
                    fontWeight='500'
                  >
                    {t('back_office.conversations.lecture_seule')}
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Zone des messages */}
            <Box
              ref={messagesContainerRef}
              flex={1}
              overflowY='auto'
              p={4}
              bg='gray.bg'
            >
              <HaliRenderMany
                many={conversationState.follow('messages').followAll('items')}
                skeleton={() => <MessageListSkeleton />}
                render={({states: messageStates}) => (
                  <ConversationMessages
                    messageStates={messageStates}
                    currentCodeUtilisateur={codeUtilisateur}
                    containerRef={messagesContainerRef}
                    emptyMessage={t('client.conversations.aucun_message')}
                  />
                )}
              />
            </Box>

            {/* Zone du bas : rejoindre (non participant) ou saisie (participant) */}
            {!isParticipant ? (
              <Box
                p={4}
                pb={{base: '5rem', md: 4}}
                bg='gray.bg'
                borderTop='1px solid'
                borderColor='gray.light'
                textAlign='center'
              >
                <Button
                  colorScheme='blue'
                  bg='blue.main'
                  color='white'
                  onClick={() => {
                    rejoindreConversation({
                      participant_type: 'utilisateur_ff',
                      code_utilisateur: codeUtilisateur!,
                    });
                  }}
                  isLoading={isRejoindreSubmitting}
                  isDisabled={!codeUtilisateur}
                  _hover={{bg: 'blue.dark'}}
                >
                  {t('back_office.conversations.rejoindre_conversation')}
                </Button>
              </Box>
            ) : (
              <ConversationMessageInputArea
                messageContent={messageContent}
                setMessageContent={setMessageContent}
                selectedFiles={selectedFiles}
                isSubmitting={isSubmitting}
                peutEnvoyer={peutEnvoyer}
                handleFileSelect={handleFileSelect}
                handleRemoveFile={handleRemoveFile}
                envoyerMessage={envoyerMessage}
                documentInputRef={documentInputRef}
                imageInputRef={imageInputRef}
                textareaRef={textareaRef}
                placeholder={
                  conversation.statut === 'terminee'
                    ? t('back_office.conversations.conversation_terminee_info')
                    : undefined
                }
                hidePieceJointeButtons={conversation.statut === 'terminee'}
                textareaMinH={
                  conversation.statut === 'terminee'
                    ? {base: '100px', md: '90px', lg: '60px'}
                    : '40px'
                }
              />
            )}
          </Box>
        );
      }}
    />
  );
};
